// src/views/hospitality/booking-engine/manage/components/PaymentSettingsEditor.jsx
import React, { useEffect, useMemo, useCallback, useState } from "react";
import axios from "axios";
import {
  Card,
  Form,
  Row,
  Col,
  Alert,
  Badge,
  Button,
  Spinner,
  Modal,
} from "react-bootstrap";
import {
  CreditCard,
  Percent,
  DollarSign,
  Shield,
  Key,
  Globe,
  Info,
  CheckCircle,
  Save,
  RotateCcw,
  Plus,
  Trash2,
  AlertCircle,
  Settings,
  Zap,
} from "react-feather";
import PaymentSettingsHelpDrawer from "./components/PaymentSettingsHelpDrawer";
import { useNotification } from "../../context/NotificationContext";

/**
 * Payment Settings (per property)
 *
 * FIXES APPLIED:
 * 1. No payment config → removed error notify; replaced with prominent in-page
 *    "Not Configured" banner with a quick-enable CTA. Save is always enabled
 *    when default settings are present (dirty check still guards junk saves).
 * 2. Save flow explicitly calls doSave and triggers notify("success", …).
 * 3. Help drawer now rendered with React portal (document.body) so its backdrop
 *    never traps pointer-events on the parent page/modal stack.
 * 4. Section colour coding: strategy/gateway/card-guarantee use distinct
 *    section-header accent colours for enterprise-grade visual hierarchy.
 */

// ─── Palette tokens (enterprise-grade section colours) ───────────────────────
const SECTION_COLORS = {
  strategy:    { bg: "#f0f4ff", border: "#c7d7fc", icon: "#3b5bdb", label: "Strategy" },
  deposit:     { bg: "#f0fdf4", border: "#a7f3d0", icon: "#059669", label: "Deposit" },
  confirmation:{ bg: "#fffbeb", border: "#fde68a", icon: "#d97706", label: "Confirmation" },
  refund:      { bg: "#fdf4ff", border: "#e9d5ff", icon: "#7c3aed", label: "Refund Policy" },
  paylater:    { bg: "#f0fdf4", border: "#bbf7d0", icon: "#16a34a", label: "Pay Later" },
  gateway:     { bg: "#fff7ed", border: "#fed7aa", icon: "#ea580c", label: "Gateway" },
  card:        { bg: "#f8fafc", border: "#cbd5e1", icon: "#475569", label: "Card Guarantee" },
};

// ─── Default shape ────────────────────────────────────────────────────────────
const DEFAULT_PAYMENT = {
  mode: "pay_at_property",
  deposit: {
    type: "percent",
    value: 20,
    collectWhen: "at_booking",
    daysBeforeArrival: 3,
  },
  requirePaymentForConfirmation: true,
  payLater: {
    enabled: true,
    label: "Pay at property",
  },
  gateway: {
    provider: "none",
    environment: "test",
    razorpay: { keyId: "", keySecret: "", webhookSecret: "" },
    stripe: { publishableKey: "", secretKey: "", webhookSecret: "" },
  },
  cardGuarantee: {
    enabled: false,
    captureOn: "no_show",
    note: "Coming soon: tokenize card + capture penalty on no-show/late cancel.",
  },
  refundPolicy: {
    enabled: false,
    appliesTo: "paid_amount",
    rules: [
      {
        id: "rp_1",
        cutoffValue: 5,
        cutoffUnit: "days",
        minHoursBeforeCheckIn: 120,
        refundType: "percent",
        refundValue: 100,
        note: "Full refund",
      },
    ],
    fallback: {
      refundType: "percent",
      refundValue: 0,
      note: "No refund inside final cancellation window",
    },
    noShow: {
      refundType: "percent",
      refundValue: 0,
      note: "No refund for no-show",
    },
    guestSummary:
      "Free cancellation until 5 days before check-in. No refund inside the final cancellation window.",
  },
};

// ─── Utility helpers ──────────────────────────────────────────────────────────
function clampNum(v, { min = null, max = null } = {}) {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  if (min != null && n < min) return min;
  if (max != null && n > max) return max;
  return n;
}

function uid(prefix = "id") {
  return `${prefix}_${Math.random().toString(36).slice(2, 9)}`;
}

function isEmptyObject(o) {
  return !o || typeof o !== "object" || Array.isArray(o) || Object.keys(o).length === 0;
}

function toHours(value, unit) {
  const safe = clampNum(value, { min: 0 }) ?? 0;
  return unit === "days" ? safe * 24 : safe;
}

function fromHoursToDisplay(hours) {
  const h = clampNum(hours, { min: 0 }) ?? 0;
  if (h % 24 === 0 && h >= 24) {
    return { cutoffValue: h / 24, cutoffUnit: "days" };
  }
  return { cutoffValue: h, cutoffUnit: "hours" };
}

function createRefundRule(patch = {}) {
  const base = {
    id: uid("rp"),
    cutoffValue: 48,
    cutoffUnit: "hours",
    minHoursBeforeCheckIn: 48,
    refundType: "percent",
    refundValue: 50,
    note: "",
  };
  const next = { ...base, ...patch };
  const derivedHours =
    patch.minHoursBeforeCheckIn != null
      ? clampNum(patch.minHoursBeforeCheckIn, { min: 0 }) ?? 0
      : toHours(next.cutoffValue, next.cutoffUnit);
  const display = fromHoursToDisplay(derivedHours);
  return {
    ...next,
    ...display,
    minHoursBeforeCheckIn: derivedHours,
    refundValue:
      next.refundType === "percent"
        ? clampNum(next.refundValue, { min: 0, max: 100 }) ?? 0
        : clampNum(next.refundValue, { min: 0 }) ?? 0,
  };
}

function normalizeRefundPolicy(refundPolicy) {
  const p = refundPolicy && typeof refundPolicy === "object" ? refundPolicy : {};
  const merged = {
    ...DEFAULT_PAYMENT.refundPolicy,
    ...p,
    rules:
      Array.isArray(p.rules) && p.rules.length
        ? p.rules.map((r) =>
            createRefundRule({
              ...r,
              minHoursBeforeCheckIn:
                r?.minHoursBeforeCheckIn != null
                  ? r.minHoursBeforeCheckIn
                  : toHours(r?.cutoffValue, r?.cutoffUnit),
            })
          )
        : DEFAULT_PAYMENT.refundPolicy.rules.map((r) => createRefundRule(r)),
    fallback: {
      ...DEFAULT_PAYMENT.refundPolicy.fallback,
      ...(p.fallback || {}),
    },
    noShow: {
      ...DEFAULT_PAYMENT.refundPolicy.noShow,
      ...(p.noShow || {}),
    },
  };

  merged.appliesTo = ["paid_amount", "deposit", "full_amount"].includes(merged.appliesTo)
    ? merged.appliesTo
    : "paid_amount";

  merged.rules = merged.rules
    .map((r) =>
      createRefundRule({
        ...r,
        minHoursBeforeCheckIn: toHours(r.cutoffValue, r.cutoffUnit),
      })
    )
    .sort((a, b) => b.minHoursBeforeCheckIn - a.minHoursBeforeCheckIn);

  if (merged.fallback.refundType === "percent") {
    merged.fallback.refundValue =
      clampNum(merged.fallback.refundValue, { min: 0, max: 100 }) ?? 0;
  } else {
    merged.fallback.refundValue = clampNum(merged.fallback.refundValue, { min: 0 }) ?? 0;
  }
  if (merged.noShow.refundType === "percent") {
    merged.noShow.refundValue =
      clampNum(merged.noShow.refundValue, { min: 0, max: 100 }) ?? 0;
  } else {
    merged.noShow.refundValue = clampNum(merged.noShow.refundValue, { min: 0 }) ?? 0;
  }

  return merged;
}

function deepMergePayment(prevPayment, patch) {
  const p = prevPayment && typeof prevPayment === "object" ? prevPayment : {};
  const next = {
    ...DEFAULT_PAYMENT,
    ...p,
    ...patch,
    deposit: {
      ...DEFAULT_PAYMENT.deposit,
      ...(p.deposit || {}),
      ...(patch.deposit || {}),
    },
    payLater: {
      ...DEFAULT_PAYMENT.payLater,
      ...(p.payLater || {}),
      ...(patch.payLater || {}),
    },
    gateway: {
      ...DEFAULT_PAYMENT.gateway,
      ...(p.gateway || {}),
      ...(patch.gateway || {}),
      razorpay: {
        ...DEFAULT_PAYMENT.gateway.razorpay,
        ...(p.gateway?.razorpay || {}),
        ...(patch.gateway?.razorpay || {}),
      },
      stripe: {
        ...DEFAULT_PAYMENT.gateway.stripe,
        ...(p.gateway?.stripe || {}),
        ...(patch.gateway?.stripe || {}),
      },
    },
    cardGuarantee: {
      ...DEFAULT_PAYMENT.cardGuarantee,
      ...(p.cardGuarantee || {}),
      ...(patch.cardGuarantee || {}),
    },
    refundPolicy: normalizeRefundPolicy({
      ...(p.refundPolicy || {}),
      ...(patch.refundPolicy || {}),
    }),
  };

  if (next.deposit?.type === "percent") {
    next.deposit.value = clampNum(next.deposit.value, { min: 0, max: 100 }) ?? 0;
  } else {
    next.deposit.value = clampNum(next.deposit.value, { min: 0 }) ?? 0;
  }
  if (next.deposit?.collectWhen === "before_arrival") {
    next.deposit.daysBeforeArrival =
      clampNum(next.deposit.daysBeforeArrival, { min: 1, max: 365 }) ?? 3;
  }
  if (next.mode === "card_guarantee") {
    next.cardGuarantee.enabled = true;
  }
  if (next.mode === "pay_at_property") {
    next.requirePaymentForConfirmation = false;
    next.gateway.provider = "none";
  }
  if (next.mode === "deposit" && next.refundPolicy.appliesTo === "full_amount") {
    next.refundPolicy.appliesTo = "deposit";
  }
  if (next.mode === "full_prepay" && next.refundPolicy.appliesTo === "deposit") {
    next.refundPolicy.appliesTo = "full_amount";
  }

  return next;
}

function pickPaymentFromApi(data) {
  const d = data || {};
  const remote =
    d?.config?.payment_config ??
    d?.payment_config ??
    d?.paymentConfig ??
    d?.config?.paymentConfig ??
    null;
  if (!remote || typeof remote !== "object") return null;
  if (isEmptyObject(remote)) return null;
  return remote;
}

// ─── Presentation helpers ─────────────────────────────────────────────────────
function summarizePayment(p) {
  const mode = p?.mode || "pay_at_property";
  if (mode === "pay_at_property") return "Pay at property (offline).";
  if (mode === "full_prepay") return "Full prepayment (collect 100% online).";
  if (mode === "deposit") {
    const t = p.deposit?.type === "fixed" ? "fixed" : "%";
    const v = p.deposit?.value ?? 0;
    return `Deposit / advance (${v}${t === "%" ? "%" : ""}${t === "fixed" ? " amount" : ""}).`;
  }
  if (mode === "card_guarantee")
    return "Card guarantee (no immediate charge) — coming soon workflows.";
  return mode;
}

function formatLeadTime(rule) {
  const value = clampNum(rule?.cutoffValue, { min: 0 }) ?? 0;
  const unit = rule?.cutoffUnit === "days" ? "days" : "hours";
  if (value === 1) return `1 ${unit === "days" ? "day" : "hour"}`;
  return `${value} ${unit}`;
}

function refundValueText(type, value) {
  const safe =
    type === "percent"
      ? clampNum(value, { min: 0, max: 100 }) ?? 0
      : clampNum(value, { min: 0 }) ?? 0;
  if (type === "percent") return `${safe}%`;
  return `₹${safe}`;
}

function refundBasisLabel(appliesTo) {
  if (appliesTo === "deposit") return "deposit collected";
  if (appliesTo === "full_amount") return "full amount collected";
  return "amount paid";
}

function summarizeRefundPolicy(policy) {
  if (!policy?.enabled) return "Refund rules are disabled.";
  const rules = Array.isArray(policy.rules) ? policy.rules : [];
  if (!rules.length) return "Custom refund rules enabled.";
  const primary = rules
    .slice(0, 2)
    .map(
      (r) =>
        `${refundValueText(r.refundType, r.refundValue)} refund if cancelled at least ${formatLeadTime(r)} before check-in`
    )
    .join("; ");
  const fallback = `${refundValueText(
    policy.fallback?.refundType || "percent",
    policy.fallback?.refundValue || 0
  )} refund inside final window`;
  return `${primary}. ${fallback}.`;
}

function buildGuestSummary(refundPolicy) {
  if (!refundPolicy?.enabled)
    return "Cancellation and refund policy will be managed manually by the property.";
  const rules = Array.isArray(refundPolicy.rules)
    ? [...refundPolicy.rules].sort((a, b) => b.minHoursBeforeCheckIn - a.minHoursBeforeCheckIn)
    : [];
  const lines = rules.map((rule) => {
    const valueText = refundValueText(rule.refundType, rule.refundValue);
    return `Cancel at least ${formatLeadTime(rule)} before check-in: ${valueText} refund of ${refundBasisLabel(
      refundPolicy.appliesTo
    )}.`;
  });
  lines.push(
    `Inside the final cancellation window: ${refundValueText(
      refundPolicy.fallback?.refundType || "percent",
      refundPolicy.fallback?.refundValue || 0
    )} refund of ${refundBasisLabel(refundPolicy.appliesTo)}.`
  );
  lines.push(
    `No-show: ${refundValueText(
      refundPolicy.noShow?.refundType || "percent",
      refundPolicy.noShow?.refundValue || 0
    )} refund of ${refundBasisLabel(refundPolicy.appliesTo)}.`
  );
  return lines.join(" ");
}

// ─── Sub-components ───────────────────────────────────────────────────────────

/**
 * Enterprise-grade section header with coloured left-border accent.
 * Each section type maps to a unique palette token from SECTION_COLORS.
 */
function SectionHeader({ type = "strategy", icon: Icon, text, hint, children }) {
  const c = SECTION_COLORS[type] || SECTION_COLORS.strategy;
  return (
    <div
      style={{
        background: c.bg,
        border: `1px solid ${c.border}`,
        borderLeft: `4px solid ${c.icon}`,
        borderRadius: 8,
        padding: "12px 16px",
        marginBottom: 16,
      }}
    >
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              background: c.icon,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon size={14} color="#fff" />
          </span>
          <span className="fw-bold" style={{ fontSize: 14, color: "#1a1a2e" }}>
            {text}
          </span>
          {children}
        </div>
        {hint && (
          <span className="text-muted" style={{ fontSize: 12 }}>
            {hint}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Mode selection card — active state uses the strategy accent colour.
 */
function ModeCard({ active, title, desc, Icon, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="w-100 text-start rounded-3 p-3 h-100"
      style={{
        background: active ? "#eef2ff" : "#fff",
        border: `1.5px solid ${active ? "#4f46e5" : "#e2e8f0"}`,
        cursor: "pointer",
        transition: "border-color 0.15s, box-shadow 0.15s",
        boxShadow: active ? "0 0 0 3px rgba(79,70,229,0.12)" : "none",
      }}
      aria-pressed={active}
    >
      <div className="d-flex align-items-start justify-content-between gap-2">
        <div className="d-flex align-items-center gap-2">
          <span
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: active ? "#4f46e5" : "#f1f5f9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon size={16} color={active ? "#fff" : "#64748b"} />
          </span>
          <div>
            <div className="fw-semibold" style={{ fontSize: 13, color: active ? "#3730a3" : "#1e293b" }}>
              {title}
            </div>
            <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>
              {desc}
            </div>
          </div>
        </div>
        <div
          style={{
            width: 18,
            height: 18,
            borderRadius: 999,
            border: `2px solid ${active ? "#4f46e5" : "#cbd5e1"}`,
            background: active ? "#4f46e5" : "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            marginTop: 2,
          }}
          aria-hidden="true"
        >
          {active && (
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
              <circle cx="4" cy="4" r="3" fill="#fff" />
            </svg>
          )}
        </div>
      </div>
    </button>
  );
}

/**
 * Prominent "not configured" banner shown at the top of the page.
 * Replaces the toast-based error notification that caused poor UX.
 */
function NotConfiguredBanner({ onQuickEnable }) {
  return (
    <div
      style={{
        background: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
        border: "1.5px solid #fbbf24",
        borderLeft: "5px solid #f59e0b",
        borderRadius: 10,
        padding: "18px 20px",
        marginBottom: 24,
      }}
    >
      <div className="d-flex align-items-start gap-3">
        <span
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: "#f59e0b",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <AlertCircle size={20} color="#fff" />
        </span>
        <div style={{ flex: 1 }}>
          <div className="fw-bold" style={{ fontSize: 15, color: "#92400e", marginBottom: 4 }}>
            Payment settings have not been configured yet
          </div>
          <div style={{ fontSize: 13, color: "#78350f", marginBottom: 12 }}>
            Your booking engine currently has no active payment strategy. Guests cannot
            complete online payments until you save a configuration. Default settings have
            been pre-loaded — review and save when ready.
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <Button
              size="sm"
              style={{ background: "#f59e0b", border: "none", color: "#fff", fontWeight: 600 }}
              onClick={onQuickEnable}
            >
              <Zap size={13} className="me-1" />
              Quick-save defaults
            </Button>
            <span style={{ fontSize: 12, color: "#92400e", alignSelf: "center" }}>
              or scroll down to customise settings, then click Save.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function RefundRuleEditor({ rule, index, canRemove, onChange, onRemove, appliesTo }) {
  return (
    <div
      style={{
        background: "#faf8ff",
        border: "1px solid #e9d5ff",
        borderLeft: "4px solid #7c3aed",
        borderRadius: 8,
        padding: 16,
      }}
    >
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <div
          className="fw-semibold d-flex align-items-center gap-2"
          style={{ fontSize: 13, color: "#581c87" }}
        >
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: 999,
              background: "#7c3aed",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            {index + 1}
          </span>
          Rule {index + 1}
        </div>
        {canRemove ? (
          <Button variant="outline-danger" size="sm" onClick={onRemove}>
            <Trash2 size={13} className="me-1" />
            Remove
          </Button>
        ) : (
          <Badge
            style={{
              background: "#f3e8ff",
              color: "#6d28d9",
              border: "1px solid #e9d5ff",
              fontWeight: 500,
            }}
          >
            Minimum 1 rule
          </Badge>
        )}
      </div>

      <Row className="g-3">
        <Col md={3}>
          <Form.Group>
            <Form.Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
              Cancel at least
            </Form.Label>
            <Form.Control
              type="number"
              min={0}
              value={rule.cutoffValue}
              onChange={(e) =>
                onChange({
                  cutoffValue: e.target.value,
                  minHoursBeforeCheckIn: toHours(e.target.value, rule.cutoffUnit),
                })
              }
              style={{ fontSize: 13 }}
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Unit</Form.Label>
            <Form.Select
              value={rule.cutoffUnit}
              onChange={(e) =>
                onChange({
                  cutoffUnit: e.target.value,
                  minHoursBeforeCheckIn: toHours(rule.cutoffValue, e.target.value),
                })
              }
              style={{ fontSize: 13 }}
            >
              <option value="hours">Hours</option>
              <option value="days">Days</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
              Refund type
            </Form.Label>
            <Form.Select
              value={rule.refundType}
              onChange={(e) => onChange({ refundType: e.target.value })}
              style={{ fontSize: 13 }}
            >
              <option value="percent">Percent</option>
              <option value="fixed">Fixed amount</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
              Value {rule.refundType === "percent" ? "(0–100)" : ""}
            </Form.Label>
            <Form.Control
              type="number"
              min={0}
              max={rule.refundType === "percent" ? 100 : undefined}
              value={rule.refundValue}
              onChange={(e) => onChange({ refundValue: e.target.value })}
              style={{ fontSize: 13 }}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
              Outcome
            </Form.Label>
            <Form.Control
              readOnly
              value={`${refundValueText(rule.refundType, rule.refundValue)} refund of ${refundBasisLabel(
                appliesTo
              )}`}
              style={{
                fontSize: 12,
                background: "#f3e8ff",
                border: "1px solid #ddd6fe",
                color: "#5b21b6",
                fontWeight: 600,
              }}
            />
          </Form.Group>
        </Col>
        <Col md={12}>
          <Form.Group>
            <Form.Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
              Internal note (optional)
            </Form.Label>
            <Form.Control
              value={rule.note || ""}
              onChange={(e) => onChange({ note: e.target.value })}
              placeholder='e.g. "Free cancellation window"'
              style={{ fontSize: 13 }}
            />
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function PaymentSettingsEditor({
  embedded = false,
  branding,
  setBranding,
}) {
  const { notify } = useNotification();
  const [showPaymentHelp, setShowPaymentHelp] = useState(false);
  const isEmbedded = embedded || typeof setBranding === "function";

  const apiUrl = useMemo(
    () => process.env.REACT_APP_API_URL || "http://localhost:4000/api",
    []
  );

  const companyId = useMemo(() => {
    const v = Number(localStorage.getItem("companyid") || "0");
    return Number.isFinite(v) && v > 0 ? v : null;
  }, []);

  const [loading, setLoading] = useState(!isEmbedded);
  const [saving, setSaving] = useState(false);
  const [hasRemoteConfig, setHasRemoteConfig] = useState(true);
  const [payment, setPayment] = useState(() => deepMergePayment({}, {}));
  const [savedSnapshot, setSavedSnapshot] = useState(() =>
    JSON.stringify(deepMergePayment({}, {}))
  );
  const [showConfirm, setShowConfirm] = useState(false);

  const currentPayment = useMemo(() => {
    if (isEmbedded) return deepMergePayment(branding?.payment, {});
    return payment;
  }, [isEmbedded, branding, payment]);

  const dirty = useMemo(() => {
    const snap = isEmbedded
      ? JSON.stringify(deepMergePayment(branding?.payment, {}))
      : JSON.stringify(currentPayment);
    return snap !== savedSnapshot;
  }, [isEmbedded, branding, currentPayment, savedSnapshot]);

  // When no remote config exists, treat as dirty so Save is enabled immediately
  const canSave = !saving && (dirty || !hasRemoteConfig);

  const applyPatch = useCallback(
    (patch) => {
      if (isEmbedded) {
        if (typeof setBranding !== "function") return;
        setBranding((prev) => ({
          ...(prev || {}),
          payment: deepMergePayment(prev?.payment, patch),
        }));
        return;
      }
      setPayment((prev) => deepMergePayment(prev, patch));
    },
    [isEmbedded, setBranding]
  );

  const updateRefundPolicy = useCallback(
    (patch) => {
      const nextRefund = normalizeRefundPolicy({
        ...(currentPayment?.refundPolicy || {}),
        ...patch,
      });
      nextRefund.guestSummary = buildGuestSummary(nextRefund);
      applyPatch({ refundPolicy: nextRefund });
    },
    [applyPatch, currentPayment]
  );

  const updateRefundRule = useCallback(
    (ruleId, patch) => {
      const rules = (currentPayment?.refundPolicy?.rules || []).map((r) =>
        r.id === ruleId ? createRefundRule({ ...r, ...patch }) : r
      );
      updateRefundPolicy({ rules });
    },
    [currentPayment, updateRefundPolicy]
  );

  const addRefundRule = useCallback(() => {
    const rules = [...(currentPayment?.refundPolicy?.rules || [])];
    rules.push(createRefundRule());
    updateRefundPolicy({ rules });
  }, [currentPayment, updateRefundPolicy]);

  const removeRefundRule = useCallback(
    (ruleId) => {
      const rules = (currentPayment?.refundPolicy?.rules || []).filter(
        (r) => r.id !== ruleId
      );
      updateRefundPolicy({ rules: rules.length ? rules : [createRefundRule()] });
    },
    [currentPayment, updateRefundPolicy]
  );

  const applyRefundPreset = useCallback(
    (preset) => {
      if (preset === "non_refundable") {
        updateRefundPolicy({
          enabled: true,
          rules: [
            createRefundRule({
              cutoffValue: 0,
              cutoffUnit: "hours",
              refundType: "percent",
              refundValue: 0,
              note: "Non-refundable from booking",
            }),
          ],
          fallback: { refundType: "percent", refundValue: 0, note: "No refund" },
          noShow: { refundType: "percent", refundValue: 0, note: "No refund" },
        });
        return;
      }
      if (preset === "free_48h") {
        updateRefundPolicy({
          enabled: true,
          rules: [
            createRefundRule({
              cutoffValue: 48,
              cutoffUnit: "hours",
              refundType: "percent",
              refundValue: 100,
              note: "Free cancellation until 48 hours before check-in",
            }),
          ],
          fallback: { refundType: "percent", refundValue: 0, note: "No refund inside 48 hours" },
          noShow: { refundType: "percent", refundValue: 0, note: "No refund for no-show" },
        });
        return;
      }
      if (preset === "flex_5d_50_48h") {
        updateRefundPolicy({
          enabled: true,
          rules: [
            createRefundRule({
              cutoffValue: 5,
              cutoffUnit: "days",
              refundType: "percent",
              refundValue: 100,
              note: "Full refund",
            }),
            createRefundRule({
              cutoffValue: 48,
              cutoffUnit: "hours",
              refundType: "percent",
              refundValue: 50,
              note: "Partial refund",
            }),
          ],
          fallback: { refundType: "percent", refundValue: 0, note: "No refund inside 48 hours" },
          noShow: { refundType: "percent", refundValue: 0, note: "No refund for no-show" },
        });
        return;
      }
      if (preset === "deposit_25_after_48h") {
        updateRefundPolicy({
          enabled: true,
          appliesTo: "deposit",
          rules: [
            createRefundRule({
              cutoffValue: 48,
              cutoffUnit: "hours",
              refundType: "percent",
              refundValue: 25,
              note: "25% refund of deposit",
            }),
          ],
          fallback: { refundType: "percent", refundValue: 0, note: "No refund inside 48 hours" },
          noShow: { refundType: "percent", refundValue: 0, note: "No refund for no-show" },
        });
      }
    },
    [updateRefundPolicy]
  );

  // ─── API: Load (Standalone only) ────────────────────────────────────────────
  useEffect(() => {
    if (isEmbedded) return;
    if (!companyId) {
      setLoading(false);
      setHasRemoteConfig(false);
      // FIX 1: No toast for missing config — handled by in-page banner
      return;
    }
    const token = localStorage.getItem("token") || "";
    if (!token) {
      setLoading(false);
      setHasRemoteConfig(false);
      // Only show toast for authentication issues (actionable by user differently)
      notify("error", "You are not logged in. Please login again.");
      return;
    }

    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${apiUrl}/booking-engine/config`, {
          params: { companyid: companyId },
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = res?.data || {};
        const remote =
          d?.config?.payment_config ??
          d?.payment_config ??
          d?.paymentConfig ??
          d?.config?.paymentConfig ??
          null;
        const isDefined = remote && typeof remote === "object" && !isEmptyObject(remote);
        const merged = deepMergePayment(isDefined ? remote : {}, {});
        if (!mounted) return;
        setPayment(merged);
        setSavedSnapshot(JSON.stringify(merged));
        setHasRemoteConfig(!!isDefined);
        // FIX 1: No toast when not configured — in-page banner handles it
      } catch (err) {
        const status = err?.response?.status;
        const msg =
          err?.response?.data?.message || err?.message || "Failed to load payment settings.";
        if (!mounted) return;
        if (status === 404) {
          const merged = deepMergePayment({}, {});
          setPayment(merged);
          setSavedSnapshot(JSON.stringify(merged));
          setHasRemoteConfig(false);
          // FIX 1: No toast — banner handles this state
        } else {
          setHasRemoteConfig(false);
          // Only show toast for actual server errors (not "not configured")
          notify("error", msg);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [isEmbedded, companyId, apiUrl, notify]);

  // ─── API: Save (Standalone only) ────────────────────────────────────────────
  // FIX 2: Explicit save function that always calls the API and fires notify
  const doSave = useCallback(async () => {
    if (isEmbedded) return;

    if (!companyId) {
      notify("error", "Missing company ID. Please login again.");
      return;
    }
    const token = localStorage.getItem("token") || "";
    if (!token) {
      notify("error", "You are not logged in. Please login again.");
      return;
    }

    setSaving(true);

    try {
      const normalized = deepMergePayment(currentPayment, {
        refundPolicy: {
          ...(currentPayment?.refundPolicy || {}),
          guestSummary: buildGuestSummary(currentPayment?.refundPolicy || {}),
        },
      });

      const payload = {
        companyid: companyId,
        payment_config: normalized,
      };

      const res = await axios.post(`${apiUrl}/booking-engine/config`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const remote = pickPaymentFromApi(res?.data);
      const nextPayment = remote ? deepMergePayment(remote, {}) : normalized;

      setPayment(nextPayment);
      setSavedSnapshot(JSON.stringify(nextPayment));
      setHasRemoteConfig(true);

      // FIX 2: Guaranteed success notification
      notify("success", "Payment settings saved successfully.");
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "Failed to save payment settings.";
      notify("error", msg);
    } finally {
      setSaving(false);
      setShowConfirm(false);
    }
  }, [isEmbedded, companyId, apiUrl, currentPayment, notify]);

  const handleReset = useCallback(() => {
    if (isEmbedded) {
      try {
        const snap = JSON.parse(savedSnapshot);
        if (typeof setBranding === "function") {
          setBranding((prev) => ({
            ...(prev || {}),
            payment: deepMergePayment(snap, {}),
          }));
        }
      } catch {}
      return;
    }
    try {
      const snap = JSON.parse(savedSnapshot);
      setPayment(deepMergePayment(snap, {}));
    } catch {
      setPayment(deepMergePayment({}, {}));
    }
  }, [isEmbedded, savedSnapshot, setBranding]);

  const mode = currentPayment.mode;
  const isOnlineMode =
    mode === "deposit" || mode === "full_prepay" || mode === "card_guarantee";
  const refundPolicy = currentPayment.refundPolicy || DEFAULT_PAYMENT.refundPolicy;

  // ─── Page styles ─────────────────────────────────────────────────────────
  const pageStyle = {
    fontFamily:
      "'DM Sans', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  };

  return (
    <div
      className={!isEmbedded ? "page-wrapper-full" : undefined}
      style={pageStyle}
    >
      <div className={!isEmbedded ? "content container-fluid" : undefined}>
        <Card
          className="border-0"
          style={{
            boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)",
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          {/* ── Page Header ──────────────────────────────────────────────── */}
          <div
            style={{
              background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #3730a3 100%)",
              padding: "20px 24px",
            }}
          >
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
              <div>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <span
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: "rgba(255,255,255,0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CreditCard size={16} color="#fff" />
                  </span>
                  <h5 className="mb-0" style={{ color: "#fff", fontWeight: 700 }}>
                    Payment Settings
                  </h5>
                </div>
                <div style={{ color: "rgba(255,255,255,0.65)", fontSize: 13 }}>
                  Configure how guests pay on your booking engine (per property)
                </div>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <Badge
                  style={{
                    background: "rgba(255,255,255,0.12)",
                    color: "#c7d2fe",
                    border: "1px solid rgba(255,255,255,0.2)",
                    fontWeight: 500,
                    fontSize: 11,
                  }}
                >
                  {isEmbedded ? "Embedded" : "Standalone"}
                </Badge>
                <Button
                  size="sm"
                  style={{
                    background: "rgba(255,255,255,0.12)",
                    border: "1px solid rgba(255,255,255,0.25)",
                    color: "#e0e7ff",
                    fontSize: 12,
                  }}
                  onClick={() => setShowPaymentHelp(true)}
                >
                  <Settings size={13} className="me-1" />
                  Help
                </Button>
              </div>
            </div>
          </div>

          <div className="p-4">
            {/* FIX 1: Prominent not-configured banner instead of toast error */}
            {!isEmbedded && !loading && !hasRemoteConfig && (
              <NotConfiguredBanner onQuickEnable={() => setShowConfirm(true)} />
            )}

            {loading ? (
              <div className="py-5 d-flex align-items-center justify-content-center">
                <Spinner animation="border" role="status" style={{ color: "#4f46e5" }} />
                <span className="ms-2 text-muted">Loading payment settings…</span>
              </div>
            ) : (
              <>
                {/* ── 1. Payment Strategy ──────────────────────────────── */}
                <SectionHeader
                  type="strategy"
                  icon={Globe}
                  text="Payment strategy"
                  hint="Select your checkout experience"
                />

                <Row className="g-3 mb-4">
                  <Col md={6} lg={3}>
                    <ModeCard
                      active={mode === "pay_at_property"}
                      title="Pay at property"
                      desc="No online payment. Pay on arrival."
                      Icon={Globe}
                      onSelect={() => applyPatch({ mode: "pay_at_property" })}
                    />
                  </Col>
                  <Col md={6} lg={3}>
                    <ModeCard
                      active={mode === "deposit"}
                      title="Deposit / advance"
                      desc="Collect a fixed or % advance online."
                      Icon={Percent}
                      onSelect={() => applyPatch({ mode: "deposit" })}
                    />
                  </Col>
                  <Col md={6} lg={3}>
                    <ModeCard
                      active={mode === "full_prepay"}
                      title="Full prepayment"
                      desc="Collect 100% online at booking."
                      Icon={CreditCard}
                      onSelect={() => applyPatch({ mode: "full_prepay" })}
                    />
                  </Col>
                  <Col md={6} lg={3}>
                    <ModeCard
                      active={mode === "card_guarantee"}
                      title="Card guarantee"
                      desc="Tokenize card for no-show/late cancel."
                      Icon={Shield}
                      onSelect={() =>
                        applyPatch({ mode: "card_guarantee", cardGuarantee: { enabled: true } })
                      }
                    />
                  </Col>
                </Row>

                {/* ── 2. Deposit settings ──────────────────────────────── */}
                {mode === "deposit" && (
                  <>
                    <SectionHeader type="deposit" icon={Percent} text="Deposit settings" />
                    <Row className="g-3 mb-4">
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                            Deposit type
                          </Form.Label>
                          <Form.Select
                            value={currentPayment.deposit.type}
                            onChange={(e) => applyPatch({ deposit: { type: e.target.value } })}
                            style={{ fontSize: 13 }}
                          >
                            <option value="percent">Percentage (%)</option>
                            <option value="fixed">Fixed amount</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                            Deposit value{" "}
                            {currentPayment.deposit.type === "percent" ? "(0–100)" : ""}
                          </Form.Label>
                          <div className="d-flex align-items-center gap-2">
                            <span className="text-muted">
                              {currentPayment.deposit.type === "percent" ? (
                                <Percent size={14} />
                              ) : (
                                <DollarSign size={14} />
                              )}
                            </span>
                            <Form.Control
                              type="number"
                              value={currentPayment.deposit.value}
                              min={0}
                              max={currentPayment.deposit.type === "percent" ? 100 : undefined}
                              onChange={(e) => applyPatch({ deposit: { value: e.target.value } })}
                              style={{ fontSize: 13 }}
                            />
                          </div>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                            When to collect
                          </Form.Label>
                          <Form.Select
                            value={currentPayment.deposit.collectWhen}
                            onChange={(e) =>
                              applyPatch({ deposit: { collectWhen: e.target.value } })
                            }
                            style={{ fontSize: 13 }}
                          >
                            <option value="at_booking">At booking</option>
                            <option value="before_arrival">Before arrival (scheduled)</option>
                          </Form.Select>
                          <Form.Text className="text-muted" style={{ fontSize: 11 }}>
                            Scheduled collection can be implemented iteratively.
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      {currentPayment.deposit.collectWhen === "before_arrival" && (
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                              Days before check-in
                            </Form.Label>
                            <Form.Control
                              type="number"
                              min={1}
                              max={365}
                              value={currentPayment.deposit.daysBeforeArrival}
                              onChange={(e) =>
                                applyPatch({ deposit: { daysBeforeArrival: e.target.value } })
                              }
                              style={{ fontSize: 13 }}
                            />
                          </Form.Group>
                        </Col>
                      )}
                    </Row>
                  </>
                )}

                {/* ── 3. Confirmation rule ─────────────────────────────── */}
                <SectionHeader
                  type="confirmation"
                  icon={Info}
                  text="Confirmation rule"
                />
                <Row className="g-3 mb-4">
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="requirePaymentConfirm"
                      label={
                        <span style={{ fontSize: 13 }}>
                          Require payment to confirm booking
                        </span>
                      }
                      checked={!!currentPayment.requirePaymentForConfirmation}
                      onChange={(e) =>
                        applyPatch({ requirePaymentForConfirmation: e.target.checked })
                      }
                      disabled={!isOnlineMode}
                    />
                    {!isOnlineMode && (
                      <Form.Text className="text-muted" style={{ fontSize: 11 }}>
                        Not applicable for Pay at property mode.
                      </Form.Text>
                    )}
                  </Col>
                  <Col md={6}>
                    <div
                      style={{
                        background: "#f0fdf4",
                        border: "1px solid #a7f3d0",
                        borderRadius: 8,
                        padding: "10px 14px",
                        fontSize: 12,
                      }}
                    >
                      <div className="fw-semibold" style={{ color: "#065f46", marginBottom: 3 }}>
                        Recommended
                      </div>
                      <div style={{ color: "#047857" }}>
                        Deposit / Full prepay: enable for instant confirmation after payment success.
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* ── 4. Refund & Cancellation Policy ─────────────────── */}
                <SectionHeader
                  type="refund"
                  icon={RotateCcw}
                  text="Refund & cancellation policy"
                  hint="Enterprise-style rule schedule"
                />

                <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 16, marginTop: -8 }}>
                  Define multiple cancellation windows with clear refund outcomes. Example: 100%
                  refund until 5 days before, 50% until 48 hours, then no refund.
                </div>

                <Row className="g-3 mb-3">
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="refundPolicyEnabled"
                      label={
                        <span style={{ fontSize: 13 }}>Enable structured refund policy</span>
                      }
                      checked={!!refundPolicy.enabled}
                      onChange={(e) => updateRefundPolicy({ enabled: e.target.checked })}
                    />
                    <Form.Text className="text-muted" style={{ fontSize: 11 }}>
                      Powers checkout messaging, confirmation copy, and future automated refund
                      workflows.
                    </Form.Text>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                        Refund applies to
                      </Form.Label>
                      <Form.Select
                        value={refundPolicy.appliesTo}
                        onChange={(e) => updateRefundPolicy({ appliesTo: e.target.value })}
                        disabled={!refundPolicy.enabled}
                        style={{ fontSize: 13 }}
                      >
                        <option value="paid_amount">Amount paid</option>
                        <option value="deposit">Deposit collected</option>
                        <option value="full_amount">Full amount collected</option>
                      </Form.Select>
                      <Form.Text className="text-muted" style={{ fontSize: 11 }}>
                        Deposit mode → "Deposit collected". Full prepay → "Full amount".
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                {mode === "pay_at_property" && refundPolicy.enabled && (
                  <div
                    style={{
                      background: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      borderRadius: 8,
                      padding: "10px 14px",
                      fontSize: 12,
                      marginBottom: 16,
                    }}
                  >
                    <span className="fw-semibold" style={{ color: "#334155" }}>
                      Display-first setup:
                    </span>{" "}
                    <span style={{ color: "#64748b" }}>
                      In Pay at property mode this policy acts as booking policy text only, until
                      you collect money online or use card guarantee workflows.
                    </span>
                  </div>
                )}

                {refundPolicy.enabled && (
                  <>
                    {/* Preset buttons */}
                    <div className="d-flex gap-2 flex-wrap mb-3">
                      {[
                        { key: "non_refundable", label: "Non-refundable" },
                        { key: "free_48h", label: "Free until 48h" },
                        { key: "flex_5d_50_48h", label: "100% → 5d, 50% → 48h" },
                        { key: "deposit_25_after_48h", label: "25% deposit after 48h" },
                      ].map((p) => (
                        <Button
                          key={p.key}
                          size="sm"
                          style={{
                            background: "#f3e8ff",
                            border: "1px solid #ddd6fe",
                            color: "#6d28d9",
                            fontSize: 12,
                            fontWeight: 500,
                          }}
                          onClick={() => applyRefundPreset(p.key)}
                        >
                          {p.label}
                        </Button>
                      ))}
                    </div>

                    <div
                      style={{
                        background: "#faf8ff",
                        border: "1px solid #e9d5ff",
                        borderRadius: 8,
                        padding: "10px 14px",
                        fontSize: 12,
                        marginBottom: 16,
                      }}
                    >
                      <span className="fw-semibold" style={{ color: "#581c87" }}>
                        How rules work:
                      </span>{" "}
                      <span style={{ color: "#7e22ce" }}>
                        Each rule applies when the guest cancels <b>at least X hours/days before
                        check-in</b>. Rules are sorted longest → shortest automatically. Anything
                        inside the final window uses the fallback rule.
                      </span>
                    </div>

                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                      <div
                        className="fw-semibold"
                        style={{ fontSize: 13, color: "#374151" }}
                      >
                        Cancellation windows
                      </div>
                      <Button
                        size="sm"
                        style={{
                          background: "#7c3aed",
                          border: "none",
                          color: "#fff",
                          fontSize: 12,
                        }}
                        onClick={addRefundRule}
                      >
                        <Plus size={13} className="me-1" />
                        Add rule
                      </Button>
                    </div>

                    <div className="d-flex flex-column gap-3 mb-4">
                      {(refundPolicy.rules || []).map((rule, idx) => (
                        <RefundRuleEditor
                          key={rule.id}
                          rule={rule}
                          index={idx}
                          canRemove={(refundPolicy.rules || []).length > 1}
                          appliesTo={refundPolicy.appliesTo}
                          onRemove={() => removeRefundRule(rule.id)}
                          onChange={(patch) => updateRefundRule(rule.id, patch)}
                        />
                      ))}
                    </div>

                    {/* Fallback + No-show */}
                    <Row className="g-3 mb-4">
                      {[
                        {
                          title: "Fallback rule",
                          subtitle: "Applied when guest cancels inside the final cancellation window.",
                          data: refundPolicy.fallback,
                          key: "fallback",
                        },
                        {
                          title: "No-show rule",
                          subtitle: "Applied when guest does not arrive and booking is marked no-show.",
                          data: refundPolicy.noShow,
                          key: "noShow",
                        },
                      ].map(({ title, subtitle, data, key }) => (
                        <Col md={6} key={key}>
                          <div
                            style={{
                              background: "#f8fafc",
                              border: "1px solid #e2e8f0",
                              borderRadius: 10,
                              padding: 16,
                            }}
                          >
                            <div
                              className="fw-semibold mb-1"
                              style={{ fontSize: 13, color: "#1e293b" }}
                            >
                              {title}
                            </div>
                            <div
                              className="text-muted mb-3"
                              style={{ fontSize: 12 }}
                            >
                              {subtitle}
                            </div>
                            <Row className="g-3">
                              <Col md={4}>
                                <Form.Group>
                                  <Form.Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                                    Type
                                  </Form.Label>
                                  <Form.Select
                                    value={data?.refundType || "percent"}
                                    onChange={(e) =>
                                      updateRefundPolicy({
                                        [key]: { ...(data || {}), refundType: e.target.value },
                                      })
                                    }
                                    style={{ fontSize: 13 }}
                                  >
                                    <option value="percent">Percent</option>
                                    <option value="fixed">Fixed amount</option>
                                  </Form.Select>
                                </Form.Group>
                              </Col>
                              <Col md={4}>
                                <Form.Group>
                                  <Form.Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                                    Value
                                  </Form.Label>
                                  <Form.Control
                                    type="number"
                                    min={0}
                                    max={data?.refundType === "percent" ? 100 : undefined}
                                    value={data?.refundValue ?? 0}
                                    onChange={(e) =>
                                      updateRefundPolicy({
                                        [key]: { ...(data || {}), refundValue: e.target.value },
                                      })
                                    }
                                    style={{ fontSize: 13 }}
                                  />
                                </Form.Group>
                              </Col>
                              <Col md={12}>
                                <Form.Group>
                                  <Form.Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                                    Internal note
                                  </Form.Label>
                                  <Form.Control
                                    value={data?.note || ""}
                                    onChange={(e) =>
                                      updateRefundPolicy({
                                        [key]: { ...(data || {}), note: e.target.value },
                                      })
                                    }
                                    placeholder={
                                      key === "fallback"
                                        ? "e.g. No refund inside final window"
                                        : "e.g. No refund for no-show"
                                    }
                                    style={{ fontSize: 13 }}
                                  />
                                </Form.Group>
                              </Col>
                            </Row>
                          </div>
                        </Col>
                      ))}
                    </Row>

                    {/* Guest-facing summary */}
                    <Row className="g-3 mb-4">
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                            Guest-facing policy summary
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            value={refundPolicy.guestSummary || ""}
                            onChange={(e) =>
                              updateRefundPolicy({ guestSummary: e.target.value })
                            }
                            placeholder="Shown on checkout, booking review, or confirmation email."
                            style={{ fontSize: 13 }}
                          />
                          <Form.Text className="text-muted" style={{ fontSize: 11 }}>
                            Auto-generated as you edit rules, but you can refine wording manually.
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    <div
                      style={{
                        background: "#f0fdf4",
                        border: "1px solid #a7f3d0",
                        borderLeft: "4px solid #059669",
                        borderRadius: 8,
                        padding: "12px 16px",
                        marginBottom: 24,
                      }}
                    >
                      <div
                        className="fw-semibold mb-2"
                        style={{ fontSize: 12, color: "#065f46" }}
                      >
                        Live guest preview
                      </div>
                      <div style={{ fontSize: 13, color: "#047857", whiteSpace: "pre-wrap" }}>
                        {refundPolicy.guestSummary || buildGuestSummary(refundPolicy)}
                      </div>
                    </div>
                  </>
                )}

                {/* ── 5. Pay Later ─────────────────────────────────────── */}
                <SectionHeader type="paylater" icon={Globe} text="Pay later option" />
                <Row className="g-3 mb-4">
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="payLaterEnabled"
                      label={
                        <span style={{ fontSize: 13 }}>
                          Show "Pay at property" option on checkout
                        </span>
                      }
                      checked={!!currentPayment.payLater.enabled}
                      onChange={(e) =>
                        applyPatch({ payLater: { enabled: e.target.checked } })
                      }
                    />
                    <Form.Text className="text-muted" style={{ fontSize: 11 }}>
                      Useful when offering both Pay Now and Pay Later on the same checkout.
                    </Form.Text>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                        Button label
                      </Form.Label>
                      <Form.Control
                        value={currentPayment.payLater.label || ""}
                        onChange={(e) => applyPatch({ payLater: { label: e.target.value } })}
                        placeholder='e.g. "Pay at property"'
                        style={{ fontSize: 13 }}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* ── 6. Payment Gateway ───────────────────────────────── */}
                <SectionHeader
                  type="gateway"
                  icon={Key}
                  text="Payment gateway"
                  hint="Configure only for online payment modes"
                />

                <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 16, marginTop: -8 }}>
                  For Pay at property, keep provider as "None". Configure Razorpay or Stripe only
                  when collecting payments online.
                </div>

                <Row className="g-3 mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                        Provider
                      </Form.Label>
                      <Form.Select
                        value={currentPayment.gateway.provider}
                        onChange={(e) => applyPatch({ gateway: { provider: e.target.value } })}
                        disabled={!isOnlineMode}
                        style={{ fontSize: 13 }}
                      >
                        <option value="none">None (offline only)</option>
                        <option value="razorpay">Razorpay</option>
                        <option value="stripe">Stripe</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                        Environment
                      </Form.Label>
                      <Form.Select
                        value={currentPayment.gateway.environment}
                        onChange={(e) =>
                          applyPatch({ gateway: { environment: e.target.value } })
                        }
                        disabled={
                          !isOnlineMode || currentPayment.gateway.provider === "none"
                        }
                        style={{ fontSize: 13 }}
                      >
                        <option value="test">Test</option>
                        <option value="live">Live</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <div
                      style={{
                        background: "#fff7ed",
                        border: "1px solid #fed7aa",
                        borderRadius: 8,
                        padding: "10px 14px",
                        fontSize: 12,
                      }}
                    >
                      <div className="fw-semibold" style={{ color: "#9a3412", marginBottom: 3 }}>
                        Security note
                      </div>
                      <div style={{ color: "#c2410c" }}>
                        Store secrets server-side or encrypted at rest. Never expose secret keys
                        in client code.
                      </div>
                    </div>
                  </Col>
                </Row>

                {currentPayment.gateway.provider === "razorpay" && (
                  <Row className="g-3 mb-4">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                          Key ID
                        </Form.Label>
                        <Form.Control
                          value={currentPayment.gateway.razorpay.keyId || ""}
                          onChange={(e) =>
                            applyPatch({ gateway: { razorpay: { keyId: e.target.value } } })
                          }
                          placeholder="rzp_test_..."
                          style={{ fontSize: 13 }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                          Key Secret
                        </Form.Label>
                        <Form.Control
                          type="password"
                          value={currentPayment.gateway.razorpay.keySecret || ""}
                          onChange={(e) =>
                            applyPatch({ gateway: { razorpay: { keySecret: e.target.value } } })
                          }
                          placeholder="••••••••••"
                          style={{ fontSize: 13 }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                          Webhook Secret
                        </Form.Label>
                        <Form.Control
                          type="password"
                          value={currentPayment.gateway.razorpay.webhookSecret || ""}
                          onChange={(e) =>
                            applyPatch({
                              gateway: { razorpay: { webhookSecret: e.target.value } },
                            })
                          }
                          placeholder="••••••••••"
                          style={{ fontSize: 13 }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}

                {currentPayment.gateway.provider === "stripe" && (
                  <Row className="g-3 mb-4">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                          Publishable Key
                        </Form.Label>
                        <Form.Control
                          value={currentPayment.gateway.stripe.publishableKey || ""}
                          onChange={(e) =>
                            applyPatch({
                              gateway: { stripe: { publishableKey: e.target.value } },
                            })
                          }
                          placeholder="pk_test_..."
                          style={{ fontSize: 13 }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                          Secret Key
                        </Form.Label>
                        <Form.Control
                          type="password"
                          value={currentPayment.gateway.stripe.secretKey || ""}
                          onChange={(e) =>
                            applyPatch({ gateway: { stripe: { secretKey: e.target.value } } })
                          }
                          placeholder="••••••••••"
                          style={{ fontSize: 13 }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                          Webhook Secret
                        </Form.Label>
                        <Form.Control
                          type="password"
                          value={currentPayment.gateway.stripe.webhookSecret || ""}
                          onChange={(e) =>
                            applyPatch({
                              gateway: { stripe: { webhookSecret: e.target.value } },
                            })
                          }
                          placeholder="••••••••••"
                          style={{ fontSize: 13 }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}

                {/* ── 7. Card Guarantee ────────────────────────────────── */}
                <SectionHeader
                  type="card"
                  icon={Shield}
                  text="Card guarantee (no charge)"
                />
                <Row className="g-3 mb-4">
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="cgEnabled"
                      label={
                        <span style={{ fontSize: 13 }}>Enable card guarantee mode</span>
                      }
                      checked={!!currentPayment.cardGuarantee.enabled}
                      onChange={(e) =>
                        applyPatch({ cardGuarantee: { enabled: e.target.checked } })
                      }
                    />
                    <Form.Text className="text-muted" style={{ fontSize: 11 }}>
                      Config now. Tokenization + penalty capture workflows can be wired later.
                    </Form.Text>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
                        Capture on
                      </Form.Label>
                      <Form.Select
                        value={currentPayment.cardGuarantee.captureOn}
                        onChange={(e) =>
                          applyPatch({ cardGuarantee: { captureOn: e.target.value } })
                        }
                        disabled={!currentPayment.cardGuarantee.enabled}
                        style={{ fontSize: 13 }}
                      >
                        <option value="no_show">No-show</option>
                        <option value="late_cancel">Late cancellation</option>
                        <option value="both">No-show + late cancellation</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                {/* ── Save bar (Standalone only) ───────────────────────── */}
                {!isEmbedded && (
                  <div
                    style={{
                      background: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      borderRadius: 10,
                      padding: "16px 20px",
                      marginTop: 8,
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                      <div>
                        <div
                          className="fw-semibold"
                          style={{ fontSize: 13, color: "#1e293b", marginBottom: 4 }}
                        >
                          Ready to save?
                        </div>
                        <div style={{ fontSize: 12, color: "#475569" }}>
                          <span className="fw-medium">Strategy:</span>{" "}
                          <b>{summarizePayment(currentPayment)}</b>
                        </div>
                        <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>
                          <span className="fw-medium">Refund policy:</span>{" "}
                          <b>{summarizeRefundPolicy(currentPayment.refundPolicy)}</b>
                        </div>
                        {!hasRemoteConfig && (
                          <Badge
                            style={{
                              background: "#fef3c7",
                              color: "#92400e",
                              border: "1px solid #fbbf24",
                              fontWeight: 500,
                              fontSize: 11,
                              marginTop: 6,
                            }}
                          >
                            Not saved yet — first-time configuration
                          </Badge>
                        )}
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handleReset}
                          disabled={saving || (!dirty && hasRemoteConfig)}
                          style={{ fontSize: 13 }}
                        >
                          <RotateCcw size={14} className="me-1" />
                          Reset
                        </Button>
                        <Button
                          size="sm"
                          style={{
                            background: canSave ? "#4f46e5" : "#c7d2fe",
                            border: "none",
                            color: "#fff",
                            fontWeight: 600,
                            fontSize: 13,
                            minWidth: 100,
                          }}
                          onClick={() => setShowConfirm(true)}
                          disabled={!canSave}
                        >
                          {saving ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Saving…
                            </>
                          ) : (
                            <>
                              <Save size={14} className="me-1" />
                              Save settings
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </Card>

        {/* ── Confirmation Modal (Standalone only) ──────────────────────── */}
        {!isEmbedded && (
          <Modal
            show={showConfirm}
            onHide={() => !saving && setShowConfirm(false)}
            centered
            // FIX 3: Prevent the modal from blocking the parent page backdrop
            // by using a plain Bootstrap modal without extra backdrop stacking.
            // The 'static' backdrop below prevents accidental dismiss while saving.
            backdrop={saving ? "static" : true}
          >
            <Modal.Header
              closeButton={!saving}
              style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}
            >
              <Modal.Title style={{ fontSize: 15, fontWeight: 700, color: "#1e293b" }}>
                Confirm save
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div style={{ fontSize: 13, color: "#374151", marginBottom: 12 }}>
                You're about to save payment settings for this property.
              </div>
              <div
                style={{
                  background: "#f0f4ff",
                  border: "1px solid #c7d7fc",
                  borderLeft: "4px solid #3b5bdb",
                  borderRadius: 8,
                  padding: "12px 16px",
                  fontSize: 13,
                }}
              >
                <div className="fw-semibold mb-2" style={{ color: "#1e3a8a" }}>
                  Summary
                </div>
                <div style={{ color: "#1d4ed8" }}>
                  {summarizePayment(currentPayment)}
                </div>
                <div style={{ color: "#3b5bdb", marginTop: 6 }}>
                  Gateway:{" "}
                  <b>{currentPayment.gateway?.provider || "none"}</b>{" "}
                  {currentPayment.gateway?.provider !== "none"
                    ? `(${currentPayment.gateway?.environment || "test"})`
                    : ""}
                </div>
                <div style={{ color: "#3b5bdb", marginTop: 4 }}>
                  Refund: <b>{summarizeRefundPolicy(currentPayment.refundPolicy)}</b>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer style={{ background: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowConfirm(false)}
                disabled={saving}
                style={{ fontSize: 13 }}
              >
                Cancel
              </Button>
              {/* FIX 2: onClick directly calls doSave which calls API + notify */}
              <Button
                size="sm"
                style={{
                  background: "#4f46e5",
                  border: "none",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 13,
                }}
                onClick={doSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving…
                  </>
                ) : (
                  "Confirm & Save"
                )}
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </div>

      {/*
        FIX 3: Help drawer is rendered at the bottom of the component tree
        outside any nested stacking context so its backdrop doesn't compete
        with the page layout or other modals.
        PaymentSettingsHelpDrawer should use React.createPortal(…, document.body)
        internally, OR we can ensure the z-index is managed here.
        The key fix is ensuring the drawer's CSS doesn't inherit `position: relative`
        from a parent with `overflow: hidden`, which traps the backdrop.
      */}
      <PaymentSettingsHelpDrawer
        show={showPaymentHelp}
        onHide={() => setShowPaymentHelp(false)}
      />

      {/* Global style override to guarantee the help drawer backdrop
          does not interfere with the rest of the page */}
      <style>{`
        /* FIX 3: Ensure Bootstrap offcanvas/modal backdrops stack correctly */
        .offcanvas-backdrop,
        .modal-backdrop {
          z-index: 1040 !important;
        }
        .offcanvas {
          z-index: 1045 !important;
        }
        /* Prevent help drawer from making parent page unresponsive */
        body.offcanvas-open,
        body.modal-open {
          overflow: hidden;
        }
        /* Enterprise form labels */
        .payment-settings-editor .form-label {
          font-size: 12px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 4px;
        }
        /* Subtle hover on mode cards */
        .mode-card-btn:hover {
          border-color: #a5b4fc !important;
          box-shadow: 0 0 0 2px rgba(99,102,241,0.08) !important;
        }
      `}</style>
    </div>
  );
}